import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
    Paper,
    Chip,
    Fade,
} from '@mui/material';
import {
    Link as LinkIcon,
    Email as EmailIcon,
    ContentCopy as CopyIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { useAdmin } from '../../admin/AdminContext';

interface CreateLinksTabProps {
    onSuccess?: () => void;
}

export default function CreateLinksTab({ onSuccess }: CreateLinksTabProps) {
    const { createCandidate } = useAdmin();
    const [candidateName, setCandidateName] = useState('');
    const [candidateEmail, setCandidateEmail] = useState('');
    const [interviewDate, setInterviewDate] = useState('');
    const [interviewTime, setInterviewTime] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!candidateName.trim() || !candidateEmail.trim()) return;

        setLoading(true);
        setError('');
        setGeneratedLink('');
        setEmailSent(false);

        const result = await createCandidate({
            candidateName: candidateName.trim(),
            candidateEmail: candidateEmail.trim(),
            interviewDate: interviewDate || undefined,
            interviewTime: interviewTime || undefined,
        });

        setLoading(false);

        if (result.ok) {
            setGeneratedLink(result.interviewLink || '');
            setEmailSent(result.emailSent || false);
            onSuccess?.();
        } else {
            setError(result.error || 'Failed to create candidate');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleReset = () => {
        setCandidateName('');
        setCandidateEmail('');
        setInterviewDate('');
        setInterviewTime('');
        setGeneratedLink('');
        setEmailSent(false);
        setError('');
        setCopied(false);
    };

    return (
        <Fade in timeout={400}>
            <Card sx={{ maxWidth: 600 }}>
                <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <LinkIcon sx={{ color: 'white' }} />
                        </Box>
                        <Box>
                            <Typography variant="h5">Send Interview Invitation</Typography>
                            <Typography variant="body2" color="text.secondary">
                                The candidate will receive an email with the interview information.
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Candidate Name"
                            value={candidateName}
                            onChange={(e) => setCandidateName(e.target.value)}
                            placeholder="Enter candidate's full name"
                            required
                            variant="outlined"
                            disabled={loading}
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={candidateEmail}
                            onChange={(e) => setCandidateEmail(e.target.value)}
                            placeholder="candidate@example.com"
                            required
                            variant="outlined"
                            disabled={loading}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                            <TextField
                                fullWidth
                                label="Interview Date"
                                type="date"
                                value={interviewDate}
                                onChange={(e) => setInterviewDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                disabled={loading}
                            />
                            <TextField
                                fullWidth
                                label="Interview Time"
                                type="time"
                                value={interviewTime}
                                onChange={(e) => setInterviewTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                disabled={loading}
                            />
                        </Stack>

                        {error && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSubmit}
                            disabled={!candidateName.trim() || !candidateEmail.trim() || loading}
                            startIcon={loading ? <PendingIcon /> : <EmailIcon />}
                            sx={{
                                py: 1.5,
                                background: 'linear-gradient(90deg, #5bbcff, #b56cff)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #4aa8eb, #a458eb)',
                                },
                                '&:disabled': {
                                    background: '#e5e7eb',
                                    color: '#9ca3af',
                                },
                            }}
                        >
                            {loading ? 'Creating...' : 'Create & Send Email'}
                        </Button>

                        {generatedLink && (
                            <Alert
                                severity="success"
                                icon={<CheckCircleIcon />}
                                sx={{
                                    borderRadius: 2,
                                    '& .MuiAlert-message': { width: '100%' }
                                }}
                            >
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Interview Link Created!
                                        </Typography>
                                        {emailSent ? (
                                            <Chip
                                                icon={<EmailIcon />}
                                                label={`Email sent to ${candidateEmail}`}
                                                color="success"
                                                size="small"
                                                sx={{ mb: 1 }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Email not configured - copy link manually"
                                                color="warning"
                                                size="small"
                                                sx={{ mb: 1 }}
                                            />
                                        )}
                                    </Box>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            wordBreak: 'break-all',
                                            fontFamily: 'monospace',
                                            fontSize: 13,
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        {generatedLink}
                                    </Paper>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<CopyIcon />}
                                            onClick={handleCopy}
                                            sx={{
                                                background: copied ? '#10b981' : '#0ea5e9',
                                                '&:hover': { background: copied ? '#059669' : '#0284c7' },
                                            }}
                                        >
                                            {copied ? 'Copied!' : 'Copy Link'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleReset}
                                        >
                                            Create Another
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Fade>
    )
}
